import { useEffect, useState, useMemo } from "react";
import { Container, Table } from "react-bootstrap";
import { useMediaQuery } from 'react-responsive';

import { checkOutRow, refreshIndexingRecords } from "../utils/Indexing";
import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { sortScripts } from '../utils/SortScripts';

import useIndexingStore from "../stores/Indexing";
import useUserStore from "../stores/User";

import { IndexingEntryForm } from "../components/IndexingEntryForm";
import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";
import { ConfirmationModal } from "../components/Confirmation";
import SortableHeader from "../components/SortableHeader";

function IndexerDashboard() {
  const isExtraLarge = useMediaQuery({ query: '(min-width: 1200px)' });

  const userData = useUserStore((state) => state.data);
  const filesWithoutIndex = useIndexingStore((state) => state.filesWithoutIndex);
  const setFilesWithoutIndex = useIndexingStore((state) => state.setFilesWithoutIndex);

  const [showForm, setShowForm] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [checkingOutFileId, setCheckingOutFileId] = useState(null);

  const [currentRow, setCurrentRow] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);

  type SortConfig = { key: string; direction: "asc" | "desc" };
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' } as SortConfig);
  const sortedIndexing = useMemo(() => sortScripts(filesWithoutIndex, sortConfig), [filesWithoutIndex, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  useEffect(() => {
    // Only fetch data if there's no data in the store
    if (!filesWithoutIndex || filesWithoutIndex.length === 0) {
      refreshIndexingRecords().then(records => {
        setFilesWithoutIndex(records);
        setIsLoadingTable(false);
      });
    } else {
      setIsLoadingTable(false);
    }
  }, [filesWithoutIndex, setFilesWithoutIndex]);

  useEffect(() => {
    if (pdfUrl !== '' && currentRow && Object.keys(currentRow).length > 0) {
      setShowForm(true);
      setIsLoadingTable(false); // Reset loading state when form is shown
      setCheckingOutFileId(null); // Reset button loading state when form is shown
    } else {
      setShowForm(false);
    }
  }, [pdfUrl, currentRow])

  return (
    <>
      <div className="hive-container d-flex">
        <div className="d-flex justify-content-between w-100">
          <div className="mx-sm-5 w-100 pb-1 pb-md-4">
            {showForm !== true ? (
              <>
                <div className="d-flex w-100 justify-content-between">
                  <h1 className="mx-3">Non-Indexed Files</h1>
                </div>

                {!isLoadingTable ? (
                  <>
                    <div className="mx-3">
                      <p>There are currently {filesWithoutIndex.length} files to index...</p>
                    </div>
                    {<Container className="mw-100 mt-4 mb-5">
                      <Table
                        striped
                        hover
                        size="sm"
                        className="indexer-dashboard-table"
                      >
                        <thead>
                          <tr>
                            <th style={{ width: "130px" }}>Check Out</th>
                            <SortableHeader
                              label="File Name"
                              sortKey="file_name"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                            />
                            {isExtraLarge && (
                              <>
                                <SortableHeader
                                  label="Date Added"
                                  sortKey="created_at"
                                  sortConfig={sortConfig}
                                  onSort={handleSort}
                                  width={130}
                                />
                                <SortableHeader
                                  label="Assigned To"
                                  sortKey="email"
                                  sortConfig={sortConfig}
                                  onSort={handleSort}
                                  width={200}
                                />
                              </>
                              )}
                            <SortableHeader
                              label="Status"
                              sortKey="index_status"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                              width={150}
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {
                            sortedIndexing.map((file, rowId) => {
                              return (
                                <tr key={rowId}>
                                  <td>
                                    <div className = "d-flex">
                                      <button
                                        disabled={file.index_user_id && file.index_user_id !== userData.user_id ? true : checkingOutFileId === file.file_id}
                                        className={`m-1 btn btn-sm flex-grow-1 ${file.index_user_id && file.index_user_id !== userData.user_id ? 'btn-secondary' : file.index_user_id && file.index_user_id === userData.user_id ? 'btn-dark' : 'btn-primary'}`}
                                        onClick={async () => {
                                          try {
                                            setCheckingOutFileId(file.file_id);
                                            const checkedOutRow = await checkOutRow(file, userData);
                                            
                                            // Only proceed if checkout was successful (returned row data)
                                            if (checkedOutRow) {
                                              // Refresh the indexing store in case the user navigates away and back
                                              await refreshIndexingRecords().then(records => {
                                                setFilesWithoutIndex(records);
                                              });

                                              const { data, error } = await supabase.storage
                                                .from("files")
                                                .createSignedUrl(file.file_name, 600);
                                              
                                              if (error) throw error;
                                              if (!data?.signedUrl) throw new Error("No signed URL returned");

                                              setCurrentRow(checkedOutRow);
                                              setPdfUrl(data.signedUrl); // This triggers useEffect and renders the form
                                            } else {
                                              // Reset loading state if checkout failed or was cancelled
                                              setCheckingOutFileId(null);
                                            }
                                          } catch (error) {
                                            console.error('Error during checkout:', error);
                                            showToast("error", `Checkout failed: ${error.message || 'Unknown error'}`);
                                            setCheckingOutFileId(null);
                                          }
                                        }}
                                      >
                                        {checkingOutFileId === file.file_id ? (
                                          <LoadingText text="Loading" className="mb-0" />
                                        ) : (
                                            file.index_user_id && file.index_user_id !== userData.user_id ? 'Checked Out'
                                              : file.index_user_id && file.index_user_id === userData.user_id ? 'Continue'
                                                : 'Check Out'
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                  <td>
                                    <span title={file.file_name}>{file.file_name}</span>
                                    {!isExtraLarge && (
                                      <>
                                        <br/>
                                        <small
                                          title={`Added ${new Date(file.created_at).toLocaleDateString()}`}
                                          className="text-muted"
                                        >
                                          {`Added ${new Date(file.created_at).toLocaleDateString()}`}
                                        </small>
                                      </>
                                    )}
                                  </td>
                                  {isExtraLarge && (
                                    <>
                                      <td>{new Date(file.created_at).toLocaleDateString()}</td>
                                      <td>{file.email ? file.email : ''}</td>
                                    </>
                                  )}
                                  <td><span title={file.index_status}>{file.email && !isExtraLarge ? file.email : file.index_status}</span></td>
                                </tr>
                              );

                            })

                          }
                        </tbody>
                      </Table>
                      <ConfirmationModal
                        show={showModal}
                        message={confirmationMessage}
                        onConfirm={pendingAction}
                        onHide={() => {
                          setShowModal(false);
                          setPendingAction(null);
                        }}
                      />
                    </Container>}
                  </>
                ) : (
                  <LoadingRing />
                )}
              </>
            ) : (
                <IndexingEntryForm 
                  url={pdfUrl} 
                  currentRow={currentRow} 
                  currentFileList={filesWithoutIndex}
                  setShowForm={setShowForm}
                  setParentLoadingState={setIsLoadingTable}
                  refreshFileList={async () => {
                    const records = await refreshIndexingRecords();
                    setFilesWithoutIndex(records);
                    setIsLoadingTable(false);
                  }}
                />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default IndexerDashboard;
